// src/encounter/services/use-save-encounter-questionnaire.ts
// FHIR-native QuestionnaireResponse SAVE for the encounter (PROD-1159 create · PROD-1354c upsert).
// Persists the WHOLE shared encounter QR (HPI/ROS/PE/Risk).
//
// Routing:
//   - `id`     → `PUT /api/questionnaire-responses/{id}` — QB's tenant-scoped update-in-place (QB #113), so an edit →
//     re-save overwrites the SAME row (no duplicate). With a known `versionId` the PUT is conditional (`If-Match`); with
//     none, it carries none (the server accepts that). A save after a sign may answer with a NEW row id (copy-on-write):
//     the caller adopts `stored.id` and `stored.meta.versionId` every time.
//   - neither  → `POST` creates; the caller adopts the returned server id so every subsequent save PUTs.
// A sign never locks the note's answers (Flutter sign model, Andrew, 14 Oct 2026), so there is no amendment route.
// QB can still refuse a write on other grounds (see `readSignLockRefusal`); the caller shows it.
import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, post, put } from '@/shared/api/api-client';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { ifMatchOf } from '../lib/answer-set';
import { useNoteWrite } from './note-session';

export interface SaveEncounterQuestionnaireInput {
  resource: QuestionnaireResponseResource;
  /** The stored version of the row (`meta.versionId`), when the page knows it: sent as `If-Match`. */
  versionId?: string;
  /**
   * The page is being hidden or unloaded (a refresh, a closed tab): send with `fetch` keepalive so the write survives it.
   */
  keepalive?: boolean;
}

/**
 * PUT `/{id}` (upsert) or POST (create); resolves to the stored resource.
 *
 * #690 (Andrew, 10 Oct 2026): each PUT of an existing row cancels the previous PUT still in flight (AbortController),
 * so an older body cannot land after the newest one on the client side. The cancelled save rejects with an AbortError
 * (`isSupersededSave`, `../lib/superseded-save`). Keyed by row id: another row's save is
 * never cancelled. A CREATE (POST) is never cancelled: a second POST would write a second row.
 */
export function useSaveEncounterQuestionnaire() {
  const noteWrite = useNoteWrite();
  const putAbortRef = useRef(new Map<string, AbortController>());
  return useMutation({
    mutationFn: ({ resource, versionId, keepalive }: SaveEncounterQuestionnaireInput) => noteWrite(() => {
      if (resource.id) {
        const key = resource.id;
        const inFlight = putAbortRef.current;
        inFlight.get(key)?.abort();
        const abort = new AbortController();
        inFlight.set(key, abort);
        return put<QuestionnaireResponseResource>(`/api/questionnaire-responses/${resource.id}`, resource, {
          signal: abort.signal,
          ...(keepalive ? { keepalive: true } : {}),
          ...(versionId ? { headers: { 'If-Match': ifMatchOf(versionId) } } : {}),
        }).finally(() => {
          if (inFlight.get(key) === abort) inFlight.delete(key);
        });
      }
      // Options only when asked for, so an ordinary create is the same call it always was.
      const opts = keepalive ? [{ keepalive: true }] as const : ([] as const);
      return post<QuestionnaireResponseResource>('/api/questionnaire-responses', resource, ...opts);
    }),
  });
}

/** QB's PHI-free refusal body for a QR write (PROD-2547). */
export interface SignLockRefusal {
  code: 'encounter-signed';
  lockState?: string;
  signedAt?: string | null;
  message?: string;
}

/**
 * The sign-lock refusal carried by a failed save, or null when the failure is anything else
 * (transport, 5xx, a 409/403 from some other cause). Only a 409 with `code: "encounter-signed"` matches,
 * so a refusal can never be mistaken for — or mask — an ordinary failure. There is no amendment route
 * (Flutter sign model, 14 Oct 2026), so no other code can occur.
 */
export function readSignLockRefusal(error: unknown): SignLockRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  try {
    const body = JSON.parse(error.body) as Partial<SignLockRefusal> | null;
    if (body && body.code === 'encounter-signed') return body as SignLockRefusal;
  } catch {
    // Not JSON — not QB's refusal.
  }
  return null;
}
